import { consume } from '@lit/context';
import { gsap } from 'gsap';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';

import type { FlagCode } from '../../../generated/flags';
import { flagSvgPath } from '../../../services/Flags';
import { gameApiContext } from '../../context';
import type { GameAPIType } from '../../GameAPI';
import { tokens } from '../../theme/tokens.css';
import { uiState as uiStateProxy, type GameMode, type ScoreboardPlayer } from '../../UIState';
import { bindValtio } from '../../ValtioController';

/** Trophy classes by rank; ranks 1–3 get a crown. */
const MEDALS = ['', 'gold', 'silver', 'bronze'];

/** A column heading with the short form portrait phones show instead. */
function headerLabel(full: string, short: string) {
  return html`<span class="label-full">${full}</span><span class="label-short">${short}</span>`;
}

/**
 * Score Panel - Detailed scoreboard with player rankings
 *
 * Modal panel displaying player rankings and statistics.
 * Supports three game modes: FFA, CTF, and BTR.
 *
 * Features:
 * - Medal badges for top 3 players (gold, silver, bronze)
 * - Team colors for CTF mode
 * - Spectating state indication (grayed out)
 * - Bot badges
 * - Current player highlighting
 * - MVP section with top performer
 * - Click player to open context menu
 * - GSAP entrance/exit animations
 * - ESC or backdrop click to close
 *
 * @element score-panel
 */
@customElement('score-panel')
export class ScorePanel extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        /* Declared here rather than inherited from the body. No text selection,
           no iOS long-press callout (a held flag offers to save the image), and
           no tap highlight — tap-to-close would flash it across the viewport. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .backdrop {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--color-bg-backdrop);
        z-index: 200;
        /* Drops the 300ms double-tap delay on tap-to-close. */
        touch-action: manipulation;
      }

      .panel {
        position: relative;
        box-sizing: border-box;
        /* 680×430 is the desktop target; clamp to the viewport so the panel
           never overflows a phone (a landscape phone is ~390px tall, below
           the 430 design height). The body is a flex column so the score
           list absorbs whatever vertical space is left after clamping. */
        width: 680px;
        height: 430px;
        max-width: calc(100vw - 24px);
        max-height: calc(100vh - 24px);
        max-height: calc(100dvh - 24px);
        display: flex;
        flex-direction: column;
        background-color: var(--color-bg-panel);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        padding: 18px;
        cursor: default;
      }

      .header {
        width: 100%;
        text-align: center;
        font-weight: bold;
        font-size: var(--font-size-xxl);
        color: var(--color-text-white);
        padding-bottom: 16px;
      }

      .player-count {
        color: var(--color-text-tooltip);
      }

      .table-header {
        display: flex;
        width: 100%;
        font-weight: bold;
        color: var(--color-text-white);
        font-size: 15px;
        padding: 6px 0;
        /* Reserves the list's scrollbar gutter so the headings stay over their
           columns (the scrollbar narrows the rows, not this row). Zero where
           scrollbars overlay the content, as on phones and macOS. */
        overflow-y: hidden;
        scrollbar-gutter: stable;
      }

      .spacer {
        height: 1px;
        background-color: var(--color-bg-selected);
        margin-top: 4px;
        margin-bottom: 6px;
      }

      .score-container {
        overflow-x: hidden;
        overflow-y: auto;
        /* The gutter stays when the list fits, so rows always match the
           header's reserved gutter. */
        scrollbar-gutter: stable;
        overscroll-behavior: contain;
        /* Fills the space between the table header and the MVP footer.
           min-height:0 lets it shrink below its content so it scrolls
           rather than pushing the panel past the clamped height. */
        flex: 1;
        min-height: 0;
        position: relative;
      }

      .table-header::-webkit-scrollbar,
      .score-container::-webkit-scrollbar {
        width: 8px;
      }

      .score-container::-webkit-scrollbar-track {
        background: var(--color-border-subtle);
        border-radius: var(--radius-sm);
      }

      .score-container::-webkit-scrollbar-thumb {
        background: var(--color-overlay-medium);
        border-radius: var(--radius-sm);
      }

      .score-container::-webkit-scrollbar-thumb:hover {
        background: var(--color-overlay-strong);
      }

      /* Column widths */
      .col-name {
        width: 38%;
      }
      .col-wins {
        width: 12%;
      }
      .col-captures {
        width: 10%;
      }
      /* Counts end against their icon at a fixed spot, so the icons form a
         column whatever the digit count (legacy's layout). */
      .col-wins.stat-cell,
      .col-captures.stat-cell {
        justify-content: flex-end;
      }
      .col-kills {
        width: 9%;
      }
      .col-deaths {
        width: 11%;
      }
      .col-damage {
        width: 13%;
      }
      .col-bounty {
        width: 12%;
        font-weight: bold;
      }
      .col-rank {
        width: 8%;
        font-weight: bold;
      }
      .col-ping {
        width: 9%;
      }

      /* Player row */
      .player-row {
        display: flex;
        width: 100%;
        padding: 6px 0;
        color: var(--color-text-bright);
        font-size: var(--font-size-md);
        line-height: 18px;
        cursor: default;
      }

      .player-row.clickable {
        cursor: pointer;
      }

      @media (hover: hover) {
        .player-row.clickable:hover {
          background-color: var(--color-border-subtle);
        }
      }

      .player-row.current-player {
        border-radius: var(--radius-xs);
        background-color: var(--color-overlay-subtle);
        color: var(--color-text-white);
      }

      .player-row.spectating {
        color: var(--color-text-tertiary);
      }

      /* Name column */
      .name-cell {
        display: flex;
        align-items: center;
        width: 38%;
      }

      /* Fixed pieces of the name cell don't shrink, so a long name truncates
         instead of pushing the rank and flag out of line. */
      .position {
        flex-shrink: 0;
        width: 30px;
        text-align: right;
        margin-right: 10px;
        font-weight: bold;
      }

      /* The crown, star and flag icons are masks filled by background-color,
         so one shape takes any colour. */
      .trophy,
      .wins-icon,
      .captures-icon {
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      .trophy {
        display: inline-block;
        width: 18px;
        height: 18px;
        vertical-align: middle;
        mask-image: url('/vanilla/ui/scoreboard/crown.svg');
        -webkit-mask-image: url('/vanilla/ui/scoreboard/crown.svg');
      }

      .trophy.gold {
        background: var(--gradient-medal-gold);
      }

      .trophy.silver {
        background: var(--gradient-medal-silver);
      }

      .trophy.bronze {
        background: var(--gradient-medal-bronze);
      }

      .flag {
        width: 20px;
        height: 16px;
        margin-right: 6px;
        flex-shrink: 0;
        object-fit: contain;
      }

      .player-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: bold;
        max-width: 130px;
      }

      .player-name.team-1 {
        color: var(--color-team-blue);
      }
      .player-name.team-2 {
        color: var(--color-team-red);
      }

      /* StarMash spectator language (same as the mini leaderboard): the name
         is struck through but keeps its identity color — the team classes
         above already outrank the row-level grey. Opacity dims without
         losing the team hue. */
      .player-name.spectating {
        text-decoration: line-through;
        opacity: 0.55;
      }

      .bot-badge {
        display: inline-block;
        flex-shrink: 0;
        background-color: rgba(70, 70, 70, 0.75);
        border-radius: var(--radius-md);
        padding: 0 4px;
        margin-left: auto;
        margin-right: 10px;
        font-weight: lighter;
        color: #ddd;
        font-size: var(--font-size-xxs);
      }

      /* Stats columns */
      .stat-cell {
        display: flex;
        align-items: center;
      }

      .stat-cell.spectating {
        color: var(--color-text-tertiary);
      }

      /* Wins/Captures icons. The right margins put each icon where legacy's
         did within its column. */
      .wins-icon,
      .captures-icon {
        flex-shrink: 0;
        width: 14px;
        height: 14px;
        margin-left: 8px;
      }

      .wins-icon {
        margin-right: 28px;
        mask-image: url('/vanilla/ui/scoreboard/star.svg');
        -webkit-mask-image: url('/vanilla/ui/scoreboard/star.svg');
        background-color: #ffd700;
      }

      .captures-icon {
        margin-right: 10px;
        mask-image: url('/vanilla/ui/scoreboard/flag.svg');
        -webkit-mask-image: url('/vanilla/ui/scoreboard/flag.svg');
        background-color: var(--color-text-white);
      }

      /* A spectator's icon greys with the rest of the row, as in legacy. */
      .stat-cell.spectating .wins-icon,
      .stat-cell.spectating .captures-icon {
        background-color: var(--color-text-tertiary);
      }

      .ping-ms {
        margin-left: 2px;
        color: var(--color-text-tertiary);
      }

      /* MVP Section */
      .mvp-section {
        font-size: 19px;
        font-weight: bold;
        text-align: center;
        color: var(--color-text-white);
        padding-top: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
      }

      /* Only the name gives way when the line is short of room. */
      .mvp-badge {
        display: inline-block;
        flex-shrink: 0;
        padding: 0 6px;
        line-height: 29px;
        border-radius: var(--radius-md);
        background-color: #2c62a9;
        text-shadow: var(--shadow-text-btn);
      }

      .mvp-flag {
        flex-shrink: 0;
        width: 32px;
        height: 24px;
        margin-right: 8px;
        object-fit: contain;
      }

      .mvp-name {
        max-width: 350px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .mvp-metric {
        flex-shrink: 0;
        white-space: nowrap;
        font-size: var(--font-size-16);
        color: var(--color-text-muted);
      }

      .label-short {
        display: none;
      }

      /* Portrait phones. The percentage columns need about 540px of row, and
         below that the headings ran together and Bounty's digits met Level's.
         Here the number columns are fixed, sized from their text at 13px, the
         headings shorten, Ping goes (your own ping is in the HUD), and the
         name takes what's left. This block stays last: at equal specificity
         the later rule wins, so above the base rules it would never apply. */
      @media (max-width: 599px) {
        .panel {
          padding: 14px 12px;
        }

        .table-header,
        .player-row {
          font-size: var(--font-size-13);
        }

        .col-name,
        .name-cell {
          flex: 1;
          min-width: 0;
          width: auto;
        }

        .position {
          width: 26px;
          margin-right: 6px;
        }

        .col-kills,
        .col-deaths {
          width: 26px;
        }
        .col-damage {
          width: 44px;
        }
        .col-bounty {
          width: 54px;
        }
        .col-rank {
          width: 24px;
        }
        .col-wins,
        .col-captures {
          width: 44px;
        }
        .wins-icon,
        .captures-icon {
          margin-left: 4px;
          margin-right: 8px;
        }
        .col-ping {
          display: none;
        }

        .label-full {
          display: none;
        }
        .label-short {
          display: inline;
        }

        .mvp-section {
          font-size: var(--font-size-16);
          padding-top: 16px;
          gap: 8px;
        }
        .mvp-flag {
          width: 28px;
          height: 21px;
          margin-right: 0;
        }
        .mvp-metric {
          font-size: var(--font-size-md);
        }
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  /** Reference to the panel element for GSAP animations */
  @query('.panel') private panel?: HTMLElement;

  /** Reference to the backdrop element for GSAP animations */
  @query('.backdrop') private backdrop?: HTMLElement;

  /** Guard against re-entrant close calls */
  private isClosing = false;

  /** @override Registers keyboard listener */
  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleKeyDown, { capture: true });
  }

  /** @override Cleans up keyboard listener and GSAP animations on disconnect */
  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeyDown, { capture: true });

    // Clean up GSAP animations
    if (this.panel) gsap.killTweensOf(this.panel);
    if (this.backdrop) gsap.killTweensOf(this.backdrop);
  }

  /** @override Triggers entrance animation. Guard, don't reset: a close that
   *  lands before first render must not be undone into a ghost entrance. */
  override firstUpdated() {
    if (this.isClosing) return;
    this.animateIn();
  }

  /** Animates the panel entrance */
  private animateIn() {
    if (!this.panel || !this.backdrop) return;

    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panel);

    gsap.fromTo(this.backdrop, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' });
    gsap.fromTo(this.panel, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' });
  }

  /** Animates the panel exit and calls onComplete when done */
  private animateOut(onComplete: () => void) {
    if (!this.panel || !this.backdrop) {
      onComplete();
      return;
    }

    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panel);

    gsap.to(this.backdrop, { opacity: 0, duration: 0.3, ease: 'power2.in' });
    gsap.to(this.panel, { opacity: 0, scale: 0.9, duration: 0.3, ease: 'power2.in', onComplete });
  }

  /** Handles Escape. Consumed (capture phase) so the in-game Escape path
   *  (UI.ts controlKey → closeAllPanels) doesn't also fire and cut the exit
   *  animation. The context menu owns Escape first while open (it consumes it
   *  in its own capture handler), so normally we only reach here with no menu —
   *  the menu check stays as a defensive fallback, closing it before the panel. */
  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    if (this.uiState.contextMenu) {
      this.api.closeContextMenu();
      return;
    }
    this.close();
  };

  /** Handles backdrop click */
  private handleBackdropClick = () => {
    this.close();
  };

  /** Handles panel click - closes context menu if open */
  private handlePanelClick = (e: Event) => {
    e.stopPropagation();
    // Close context menu if clicking on panel background (not on a player row)
    if (this.uiState.contextMenu) {
      this.api.closeContextMenu();
    }
  };

  /** Closes the panel with animation */
  private close() {
    if (this.isClosing) return;
    this.isClosing = true;
    // Close context menu immediately (before animation)
    this.api.closeContextMenu();
    this.animateOut(() => this.api.closeScorePanel());
  }

  /** Opens the player context menu from a row, on left or right click. One
   *  handler for every row, reading the player id from its data attribute, so
   *  Lit keeps a stable listener instead of re-binding fresh closures on every
   *  render (this re-renders on any uiState change). Mirrors mini-leaderboard. */
  private handleRowClick = (e: MouseEvent): void => {
    e.stopPropagation();
    if (e.type === 'contextmenu') e.preventDefault();
    const row = e.currentTarget;
    if (!(row instanceof HTMLElement)) return;
    const playerId = Number(row.dataset['playerId']);
    if (!Number.isFinite(playerId)) return;
    // The menu is position: fixed, so it takes viewport coordinates: the
    // row's top, just in from its left edge.
    const rowRect = row.getBoundingClientRect();
    this.api.playClick();
    this.api.showContextMenu(playerId, rowRect.top, rowRect.left + 20);
  };

  /** Gets the SVG path for a flag ISO code */
  private getFlagSrc(flag: FlagCode): string {
    return flagSvgPath(flag);
  }

  /** Renders the table header based on game mode */
  private renderTableHeader(gameMode: GameMode) {
    return html`
      <div class="table-header">
        <div class="col-name">&nbsp;</div>
        ${gameMode === 'btr' ? html`<div class="col-wins">&nbsp;</div>` : nothing}
        ${gameMode === 'ctf' ? html`<div class="col-captures">&nbsp;</div>` : nothing}
        <div class="col-kills">${headerLabel('Kills', 'K')}</div>
        <div class="col-deaths">${headerLabel('Deaths', 'D')}</div>
        ${gameMode === 'ffa' ? html`<div class="col-damage">${headerLabel('Damage', 'Dmg')}</div>` : nothing}
        <div class="col-bounty">Bounty</div>
        <div class="col-rank">${headerLabel('Level', 'Lvl')}</div>
        <div class="col-ping">Ping</div>
      </div>
    `;
  }

  /** Renders a single player row */
  private renderPlayerRow(player: ScoreboardPlayer, gameMode: GameMode) {
    const isTopThree = player.rank <= 3;
    const spectatingClass = player.isSpectating ? 'spectating' : '';
    const canClick = !player.isMe && !player.isBot;

    // Team class for CTF mode
    let teamClass = '';
    if (gameMode === 'ctf' && player.team > 0) {
      teamClass = `team-${player.team}`;
    }

    return html`
      <div
        class=${classMap({
          'player-row': true,
          'current-player': player.isMe,
          clickable: canClick,
          spectating: player.isSpectating,
        })}
        data-player-id=${player.id}
        @click=${canClick ? this.handleRowClick : nothing}
        @contextmenu=${canClick ? this.handleRowClick : nothing}
      >
        <!-- Name column -->
        <div class="name-cell">
          <div class="position">
            ${isTopThree ? html`<div class="trophy ${MEDALS[player.rank]}"></div>` : `${player.rank}.`}
          </div>
          <img class="flag" src="${this.getFlagSrc(player.flag)}" alt="" title=${player.flagLabel} draggable="false" />
          <div class="player-name ${teamClass} ${spectatingClass}">${player.name}</div>
          ${player.isBot ? html`<span class="bot-badge">bot</span>` : nothing}
        </div>

        <!-- BTR: Wins column -->
        ${
          gameMode === 'btr'
            ? html`
                <div class="col-wins stat-cell ${spectatingClass}">
                  ${
                    player.wins && player.wins > 0
                      ? html`${player.wins}
                          <div class="wins-icon"></div>`
                      : html`&nbsp;`
                  }
                </div>
              `
            : nothing
        }

        <!-- CTF: Captures column -->
        ${
          gameMode === 'ctf'
            ? html`
                <div class="col-captures stat-cell ${spectatingClass}">
                  ${
                    player.captures && player.captures > 0
                      ? html`${player.captures}
                          <div class="captures-icon"></div>`
                      : html`&nbsp;`
                  }
                </div>
              `
            : nothing
        }

        <!-- Common stats -->
        <div class="col-kills stat-cell ${spectatingClass}">${player.kills}</div>
        <div class="col-deaths stat-cell ${spectatingClass}">${player.deaths}</div>

        <!-- FFA: Damage column -->
        ${
          gameMode === 'ffa'
            ? html`<div class="col-damage stat-cell ${spectatingClass}">${this.formatDamage(player.damage ?? 0)}</div>`
            : nothing
        }

        <div class="col-bounty stat-cell ${spectatingClass}">${player.score}</div>
        <div class="col-rank stat-cell ${spectatingClass}">
          ${(player.level ?? 0) <= 0 ? html`&nbsp;` : player.level}
        </div>
        <div class="col-ping stat-cell ${spectatingClass}">${player.ping}<span class="ping-ms">ms</span></div>
      </div>
    `;
  }

  /** Formats damage for the rows and the MVP line alike — legacy's single
   *  formatMostAwesomeMetric, so the two can't disagree. */
  private formatDamage(value: number): string {
    const rounded = Math.round(value);
    if (rounded < 1000) return String(rounded);
    if (rounded < 100000) return `${(rounded / 1000).toFixed(1)} K`;
    if (rounded < 1000000) return `${Math.round(rounded / 1000)} K`;
    return `${(rounded / 1000000).toFixed(1)} M`;
  }

  /** Renders the MVP section */
  private renderMvp(gameMode: GameMode) {
    const mvp = this.uiState.scoreboard?.mvp;
    if (!mvp) return nothing;
    // FFA's metric is damage; CTF captures and BTR kills are plain counts.
    const value = gameMode === 'ffa' ? this.formatDamage(mvp.value) : String(mvp.value);

    return html`
      <div class="mvp-section">
        <div class="mvp-badge">MVP</div>
        <img class="mvp-flag" src="${this.getFlagSrc(mvp.flag)}" alt="" draggable="false" />
        <div class="mvp-name">${mvp.name}</div>
        <div class="mvp-metric">&bull;&nbsp;&nbsp;${value} ${mvp.metricLabel}</div>
      </div>
    `;
  }

  /** @override Renders the modal panel */
  override render() {
    const scoreboard = this.uiState.scoreboard;
    if (!scoreboard) return nothing;

    // Filter out bots if ignoreBots is enabled
    const players = this.uiState.gameSettings.ignoreBots
      ? scoreboard.players.filter((p) => !p.isBot)
      : scoreboard.players;
    const playerCount = players.length;
    const playerCountText = playerCount > 1 ? ` • ${playerCount} players` : '';

    return html`
      <div class="backdrop" @click=${this.handleBackdropClick}>
        <div class="panel" @click=${this.handlePanelClick}>
          <div class="header">${scoreboard.roomName}<span class="player-count">${playerCountText}</span></div>
          ${this.renderTableHeader(scoreboard.gameMode)}
          <div class="spacer"></div>
          <div class="score-container">
            ${repeat(
              players,
              (p) => p.id,
              (p) => this.renderPlayerRow(p, scoreboard.gameMode),
            )}
          </div>
          ${this.renderMvp(scoreboard.gameMode)}
        </div>
      </div>
    `;
  }
}
